<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-agency-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-office-building</v-icon>
          Edit Agency Profile
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Update your agency logo and basic information
        </div>
      </v-card-text>

      <!-- Error/Success message with smooth transition -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="
              messageType === 'success'
                ? 'mdi-check-circle'
                : 'mdi-alert-circle'
            "
            dismissible
            @input="clearMessage"
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Main content -->
      <v-card-text class="px-6 py-2">
        <v-row>
          <v-col cols="12" class="d-flex flex-column align-center py-4">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-3">
              Agency Logo
            </div>
            <editable-avatar
              :image="getAvatarImage"
              :onImageSelected="onImageSelected"
              :size="140"
              :editable="true"
              class="elevation-4 rounded-circle"
            />
            <div class="text-caption text--secondary mt-2">
              Click on the avatar to upload a new logo
            </div>
          </v-col>

          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.name"
              label="Agency Name*"
              outlined
              dense
              prepend-inner-icon="mdi-office-building"
              :rules="[rules.required]"
              @input="onChange"
              class="modern-input"
              background-color="white"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between flex-wrap">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!profile.name || (!profile.image && !selectedImage)"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";

export default {
  components: { EditableAvatar },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      selectedImage: null,
      selectedImagePreview: null,
      rules: {
        required: value => !!value || "This field is required"
      },
      profile: {}
    };
  },
  computed: {
    ...mapState("auth", {
      myProfile: "profile"
    }),
    getAvatarImage() {
      // If we have a selected image preview, show that first
      if (this.selectedImagePreview) {
        return this.selectedImagePreview;
      }
      // Otherwise show the profile image
      if (this.profile && this.profile.midThumbnail) {
        return this.profile.midThumbnail;
      }
      if (this.profile && this.profile.image) {
        return this.profile.image;
      }
      return require("@/assets/default.jpg");
    }
  },
  methods: {
    ...mapActions("auth", {
      updateProfile: "updateProfile"
    }),
    clearMessage() {
      this.message = null;
    },
    onChange() {
      this.message = null;
    },
    onImageSelected(image) {
      // Handle both File object and data URL
      if (image instanceof File) {
        // If it's a File, create a preview URL
        this.selectedImage = image;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.selectedImagePreview = e.target.result;
          this.message = null;
        };
        reader.readAsDataURL(image);
      } else if (typeof image === 'string') {
        // If it's already a URL or data URL
        this.selectedImage = image;
        this.selectedImagePreview = image;
        this.message = null;
      }
    },
    async onSave() {
      if (!this.profile.name) {
        this.message = "Please enter your agency name";
        this.messageType = "error";
        return;
      }

      // Check if we have an image to upload
      if (!this.selectedImage && !this.profile.image) {
        this.message = "Please select an agency logo";
        this.messageType = "error";
        return;
      }

      const formData = new FormData();
      
      // Handle image upload properly
      if (this.selectedImage) {
        // If selectedImage is a File object (from file input)
        if (this.selectedImage instanceof File) {
          formData.append("image", this.selectedImage);
        } 
        // If selectedImage is a data URL, we need to convert it to a Blob
        else if (typeof this.selectedImage === 'string' && this.selectedImage.startsWith('data:image')) {
          try {
            const response = await fetch(this.selectedImage);
            const blob = await response.blob();
            const file = new File([blob], 'agency-logo.png', { type: 'image/png' });
            formData.append("image", file);
          } catch (error) {
            console.error("Error converting data URL to file:", error);
            this.message = "Error processing image. Please try again.";
            this.messageType = "error";
            return;
          }
        }
        // If it's already a URL string (not data URL)
        else if (typeof this.selectedImage === 'string' && this.selectedImage.startsWith('http')) {
          // If it's an existing URL, we don't need to upload it again
          // But we still need to update the name
          // We'll handle this case separately
        }
      }

      // Add name to form data
      formData.append("name", this.profile.name);

      // Log what we're sending (for debugging)
      console.log("Sending form data:", {
        name: this.profile.name,
        hasImage: !!this.selectedImage,
        imageType: this.selectedImage ? 
          (this.selectedImage instanceof File ? 'File' : 
           typeof this.selectedImage === 'string' ? 'string' : 'unknown') : 'none'
      });

      this.loading = true;
      try {
        const response = await this.updateProfile(formData);
        
        // Update local profile with new data
        if (response && response.data) {
          this.profile = { ...this.profile, ...response.data };
        }
        
        this.message = "Agency profile updated successfully!";
        this.messageType = "success";
        this.selectedImage = null;
        this.selectedImagePreview = null;
        
        // Close dialog after delay
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        console.error("Update error:", error);
        this.message = error.response?.data?.message || "An error occurred while updating";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.selectedImage = null;
      this.selectedImagePreview = null;
      this.message = null;
      this.loading = false;
    }
  },
  watch: {
    myProfile: {
      handler(newValue) {
        if (newValue) {
          this.profile = { ...newValue };
          // Reset preview when profile changes
          if (!this.selectedImagePreview) {
            this.selectedImagePreview = null;
          }
        }
      },
      deep: true,
      immediate: true
    },
    dialog(val) {
      if (!val) {
        // Reset when dialog closes
        this.resetForm();
      } else {
        // Refresh profile data when dialog opens
        this.profile = { ...this.myProfile };
        this.selectedImagePreview = null;
        this.selectedImage = null;
      }
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
    this.message = null;
  }
};
</script>

<style scoped>
.modern-agency-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

@media (max-width: 400px) {
  .modern-agency-dialog {
    border-radius: 12px !important;
  }
}

.editor-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-input ::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  background-color: white !important;
}

.modern-input ::v-deep .v-messages {
  min-height: 0;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
  border-radius: 20px !important;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active:not(:disabled) {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-agency-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-agency-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-agency-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-agency-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}

/* Responsive */
@media (max-width: 599px) {
  .v-card-actions {
    flex-direction: column-reverse;
    padding: 12px 16px !important;
  }
  
  .v-card-actions .v-btn {
    width: 100% !important;
    margin: 4px 0 !important;
    justify-content: center;
  }
  
  .v-card-title {
    padding: 12px 16px !important;
  }
  
  .v-card-text {
    padding: 12px 16px !important;
  }
}
</style>